import React from 'react';
import { useTranslations } from 'src/contexts/TranslationContext';
import CommonPage from 'src/features/about/CommonPage';
import { useParams } from 'react-router-dom';

const Terms: React.FC = () => {
  const { lang } = useParams<{ lang: string }>();
  const { translations: t, language, isLoading } = useTranslations();
  const [hasTranslations, setHasTranslations] = React.useState(false);
  const [attemptCount, setAttemptCount] = React.useState(0);

  React.useEffect(() => {
    const termsData = t?.terms || t?.navigations?.terms;
    const hasTermsTranslations = !!termsData;
    setHasTranslations(hasTermsTranslations);

    if (!isLoading && t && !hasTermsTranslations && attemptCount < 3) {
      setAttemptCount(prev => prev + 1);
    }
  }, [t, language, isLoading]);

  React.useEffect(() => {
    if (isLoading) {
      const timeoutId = setTimeout(() => {
        if (isLoading) setHasTranslations(true);
      }, 3000);
      return () => clearTimeout(timeoutId);
    }
  }, [isLoading]);

  React.useEffect(() => {
    setAttemptCount(0);
  }, [language]);

  React.useEffect(() => {
    const forceDisplayTimer = setTimeout(() => {
      setHasTranslations(true);
    }, 5000);
    return () => clearTimeout(forceDisplayTimer);
  }, []);

  if ((isLoading || !hasTranslations) && attemptCount < 3) {
    return (
      <div className="max-w-6xl mx-auto p-8 text-center">
        <h2 className="text-2xl font-semibold mb-4">Loading Terms of Service...</h2>
        <p className="text-gray-500">Please wait while we load the content in {language}...</p>
        {attemptCount > 0 && (
          <p className="mt-4 text-xs text-gray-400">Attempt {attemptCount + 1}/3...</p>
        )}
      </div>
    );
  }

  const termsContent = t?.terms || t?.navigations?.terms || {};

  const content = (
    <div className="prose max-w-none">
      <h2 className="text-2xl font-semibold mb-4">
        {termsContent.title || 'Terms of Service'}
      </h2>

      <p className="mb-4 text-gray-500">
        {termsContent.lastUpdated || 'Last Updated: May 3, 2025'}
      </p>

      <section className="mb-6">
        <p>{termsContent.introduction || 'These Terms of Service ("Terms") govern your access to and use of the Global24 website. By accessing or using our service, you agree to be bound by these Terms.'}</p>
      </section>

      <section className="mb-6">
        <h3 className="text-xl font-semibold mb-2">{termsContent.acceptanceTitle || 'Acceptance of Terms'}</h3>
        <p>{termsContent.acceptanceDesc || 'By accessing or using Global24, you agree to be bound by these Terms of Service. If you do not agree to all of these terms, you may not use our services.'}</p>
      </section>

      <section className="mb-6">
        <h3 className="text-xl font-semibold mb-2">{termsContent.changesTitle || 'Changes to Terms'}</h3>
        <p>{termsContent.changesDesc || 'We may revise these terms at any time without notice. By continuing to use our website after any changes, you accept the revised terms.'}</p>
      </section>

      <section className="mb-6">
        <h3 className="text-xl font-semibold mb-2">{termsContent.accountsTitle || 'User Accounts'}</h3>
        <p>{termsContent.accountsDesc || 'When you create an account with us, you must provide accurate, complete, and current information. You are responsible for safeguarding the password that you use to access our service and for any activities under your account.'}</p>
      </section>

      <section className="mb-6">
        <h3 className="text-xl font-semibold mb-2">{termsContent.contentTitle || 'Content and Intellectual Property'}</h3>
        <p>{termsContent.contentDesc || 'All content on our website, including text, graphics, logos, images, and software, is the property of Global24 or its content suppliers and is protected by international copyright laws.'}</p>
      </section>

      <section className="mb-6">
        <h3 className="text-xl font-semibold mb-2">{termsContent.userContentTitle || 'User-Generated Content'}</h3>
        <p>{termsContent.userContentDesc || 'By posting content on our site, you grant us a non-exclusive, royalty-free, perpetual, and worldwide license to use, reproduce, modify, and distribute your content in connection with our services.'}</p>
      </section>

      <section className="mb-6">
        <h3 className="text-xl font-semibold mb-2">{termsContent.conductTitle || 'Content and Conduct'}</h3>
        <p>{termsContent.conductDesc || 'Our service allows you to post, link, store, share, and otherwise make available certain information, text, graphics, or other material. You are responsible for the content that you post to the service, including its legality, reliability, and appropriateness.'}</p>
      </section>

      <section className="mb-6">
        <h3 className="text-xl font-semibold mb-2">{termsContent.prohibitedTitle || 'Prohibited Uses'}</h3>
        <p>You agree not to use the service in any way that:</p>
        <ul className="list-disc pl-6">
          {Array.isArray(termsContent.prohibitedList) ? (
            termsContent.prohibitedList.map((item: string, index: number) => (
              <li key={index}>{item}</li>
            ))
          ) : (
            <>
              <li>Violates any law, regulation, or legal right</li>
              <li>Is harmful, fraudulent, deceptive, or offensive</li>
              <li>Attempts to impersonate another person or entity</li>
              <li>Infringes any patent, trademark, trade secret, copyright, or other intellectual rights</li>
              <li>Transmits any viruses, malware, or other malicious code</li>
            </>
          )}
        </ul>
      </section>

      <section className="mb-6">
        <h3 className="text-xl font-semibold mb-2">{termsContent.disclaimerTitle || 'Disclaimer of Warranties'}</h3>
        <p>{termsContent.disclaimerDesc || "Our website is provided 'as is' without any warranties, expressed or implied. We do not guarantee that our services will be error-free, secure, or continuously available."}</p>
      </section>

      <section className="mb-6">
        <h3 className="text-xl font-semibold mb-2">{termsContent.limitationTitle || 'Limitation of Liability'}</h3>
        <p>{termsContent.limitationDesc || 'Global24 shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of or inability to use our services.'}</p>
      </section>

      <section className="mb-6">
        <h3 className="text-xl font-semibold mb-2">{termsContent.governingLawTitle || 'Governing Law'}</h3>
        <p>{termsContent.governingLawDesc || 'These Terms shall be governed by the laws of the United States, without regard to its conflict of law provisions.'}</p>
      </section>

      <section className="mb-6">
        <h3 className="text-xl font-semibold mb-2">{termsContent.contactTitle || 'Contact Information'}</h3>
        <p>{termsContent.contactDesc || 'If you have any questions about these Terms, please contact us at terms@global24.news.'}</p>
      </section>
    </div>
  );

  const pageTitle = termsContent.title || 'Terms of Service';
  const pageDescription = termsContent.introduction || 'Global24 is your trusted source for real-time global news, delivering breaking stories with unmatched speed and accuracy.';

  return (
    <CommonPage
      lang={lang}
      pageType="terms"
      title={pageTitle}
      metaDescription={pageDescription}
      content={content}
    />
  );
};

export default Terms;
